// RecordTable.jsx
import React, { useMemo, useState, useEffect } from 'react';
import Classes from './RecordTable.module.css';
import useScreenWidth from '../../../../../../Hooks/useScreenWidth';
import SkeletonLoading from '../../../../../GlobalComponents/SkeletonLoading/SkeletonLoading';
import { Pagination, Stack } from '@mui/material';
import TableRow from './TableRow';
import { useNavigate } from 'react-router-dom';
import { AddIcon } from '../../../../../../utils/Icons';

/* ─────────── Date & time helpers ─────────── */

// Parse plain "YYYY-MM-DD" safely as local time.
function parseYMDToLocal(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!m) return null;
  const [_, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d), 0, 0, 0, 0);
}

// Safe date parse for Date | string | null
function safeDate(input) {
  if (!input) return null;
  if (input instanceof Date) return Number.isFinite(input.getTime()) ? input : null;
  // If plain Y-M-D, parse as local
  const local = parseYMDToLocal(input);
  if (local) return local;
  // Else let Date handle ISO / RFC strings
  const dt = new Date(input);
  return Number.isFinite(dt.getTime()) ? dt : null;
}

// Format a date (local) as "MMM d, yyyy"
function fmt(dateish) {
  const dt = safeDate(dateish);
  if (!dt) return '—';
  return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ---- time parsing & 12h display for "timeSlot" labels ----
function toHHMM(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
function to12hFromMin(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}
function toMin(hhmm) {
  const [h, m] = String(hhmm).split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}
// Parse "H:MM AM/PM" or "HH:MM" → minutes since midnight
function parseTimeToMin(token) {
  if (!token) return null;
  const s = String(token).trim();
  const m12 = s.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
  if (m12) {
    let hh = Number(m12[1]);
    const mm = Number(m12[2]);
    const ap = m12[3].toUpperCase();
    if (hh === 12) hh = 0;
    const h24 = ap === 'PM' ? hh + 12 : hh;
    return h24 * 60 + mm;
  }
  const m24 = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m24) {
    const hh = Number(m24[1]);
    const mm = Number(m24[2]);
    if (hh >= 0 && hh < 24 && mm >= 0 && mm < 60) return hh * 60 + mm;
  }
  return null;
}
// Convert "HH:MM — HH:MM" or "H:MM AM — H:MM PM" to 12h; no-op if already AM/PM
function fmtTimeSlot12h(label) {
  if (!label) return '';
  if (/(am|pm)/i.test(label)) return label;
  const sep = label.includes('—') ? '—' : (label.includes('-') ? '-' : null);
  if (sep) {
    const [a, b] = label.split(sep).map((s) => s.trim());
    const ma = parseTimeToMin(a);
    const mb = parseTimeToMin(b);
    if (ma != null && mb != null) return `${to12hFromMin(ma)} ${sep} ${to12hFromMin(mb)}`;
  }
  // single time
  const m = parseTimeToMin(label);
  return m != null ? to12hFromMin(m) : label;
}

// Combine appointment date + start of timeSlot (if any) → timestamp (ms)
function apptToMs(appt) {
  if (!appt) return 0;
  const d = safeDate(appt.date);
  if (!d) return 0;
  // get start minutes if available
  let startMin = null;
  if (appt.timeSlot) {
    const first = String(appt.timeSlot).split(/—|-|to|–/i)[0].trim();
    startMin = parseTimeToMin(first);
  }
  const base = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const ms = base.getTime() + ((startMin ?? 0) * 60 * 1000);
  return ms;
}

/* ─────────── Membership helpers ─────────── */
const labelFor = (key) => (!key || key === 'free' ? 'None' : key?.charAt(0).toUpperCase() + key?.slice(1));

function deriveMembership(userBundle) {
  const u = userBundle?.user || {};
  const sub = u?.activeSubscription || {};
  const status = (sub?.status || 'none').toLowerCase();
  const start = safeDate(sub?.current_period_start);
  const end   = safeDate(sub?.current_period_end);
  const planKey = sub?.plan?.key || u?.subscription || 'free';
  const now = Date.now();

  const inWindow = start && end && start.getTime() <= now && end.getTime() >= now;
  const isCanceled = status === 'canceled';
  const isNone = status === 'none' || !planKey || planKey === 'free';
  const isExpired = !!end && end.getTime() < now && !isCanceled;

  const isActive = !isNone && !isCanceled && inWindow;

  let statusText = 'None';
  if (isActive) statusText = 'Active';
  else if (sub?.cancel_at_period_end && inWindow) statusText = 'Cancels at period end';
  else if (isCanceled) statusText = 'Canceled';
  else if (isExpired) statusText = 'Expired';
  else statusText = 'None';

  return {
    planLabel: labelFor(planKey),
    statusText,
    periodStart: start ? fmt(start) : '—',
    periodEnd: end ? fmt(end) : '—',
    rawStatus: status,
  };
}

/* ─────────── Component ─────────── */

export default function RecordTable({ loading, items = [] }) {
  const navigateTo = useNavigate();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 480;

  const rows = useMemo(() => {
    return (items || []).map((bundle) => {
      const u = bundle.user || {};
      const name = u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || '—';
      const address = u.customerProfile?.location?.address || '—';
      const phone = u.phone || '—';

      const bookings = Array.isArray(bundle.bookings) ? bundle.bookings : [];

      const completed = bookings
        .filter((b) => b.status === 'completed')
        .sort((a, b) => apptToMs(a.appointment) - apptToMs(b.appointment));

      const upcoming = bookings
        .filter((b) => ['upcoming', 'on_the_way', 'delayed'].includes(b.status))
        .sort((a, b) => apptToMs(a.appointment) - apptToMs(b.appointment));

      // Last delivery (latest completed)
      let lastDelivery = '—';
      if (completed.length) {
        const appt = completed[completed.length - 1]?.appointment || {};
        const datePart = fmt(appt?.date);
        const slot = appt?.timeSlot ? fmtTimeSlot12h(appt.timeSlot) : '';
        lastDelivery = slot ? `${datePart}` : datePart;
      }

      // Next delivery (earliest upcoming)
      let nextDelivery = '—';
      if (upcoming.length) {
        const appt = upcoming[0]?.appointment || {};
        const datePart = fmt(appt?.date);
        const slot = appt?.timeSlot ? fmtTimeSlot12h(appt.timeSlot) : '';
        nextDelivery = slot ? `${datePart}` : datePart;
      }

      const membership = deriveMembership(bundle);

      return {
        _id: u._id,
        name,
        address,
        phone,
        lastDelivery,
        nextDelivery,
        membership,
        __raw: bundle,
      };
    });
  }, [items]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const totalPages = Math.max(1, Math.ceil(rows.length / itemsPerPage));
  const currentItems = rows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const startItem = rows.length ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, rows.length);
  const totalItems = rows.length;

  useEffect(() => setCurrentPage(1), [rows.length]);

  return (
    <div className={Classes.hero}>
      <div className={Classes.table}>
        <div className={Classes.tableWrapper}>

          <div className={Classes.tableBanner}>
            <div className='flexAlign10'>
              <div className={Classes.tableBannerItem}>
                <h5>Clients</h5>
                <h4>{loading ? '…' : rows.length}</h4>
              </div>
            </div>
            <div>
              <button className={Classes.addBtn} onClick={() => { navigateTo('/add-client'); }}>
                <AddIcon height='12px' fill='white' />
                <h5>Add client</h5>
              </button>
            </div>
          </div>

          <div className={Classes.tableHeader}>
            <div className={`${Classes.headerItem} ${Classes.header1}`}><h5>Name</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header2}`}><h5>Address</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header3}`}><h5>Phone Number</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header4}`}><h5>Last Delivery</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header5}`}><h5>Next Delivery</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header6}`}><h5>Membership</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header7}`} />
          </div>

          <div className={Classes.tableBody}>
            {loading ? (
              <SkeletonLoading length={itemsPerPage} height={isMobile ? '3.5rem' : '2.8rem'} width="100%" br="6px" padding='0' />
            ) : currentItems.length ? (
              currentItems.map((row, idx) => (
                <TableRow key={row._id ?? idx} value={row} index={idx} />
              ))
            ) : (
              <h5 className='placeholderText' style={{ margin: '15px 19px' }} >No customer yet...</h5>
            )}
          </div>

          { currentItems && currentItems.length > 0 &&
            <div className={Classes.pagFooter}>
              <h5 style={{ height: '1.2rem' }}>
                {loading
                  ? <SkeletonLoading height='1.2rem' width='10rem' br='4px' padding='0' />
                  : `Showing ${startItem} to ${endItem} of ${totalItems} results`
                }
              </h5>
              <Stack spacing={2} className={Classes.pagination}>
                {loading
                  ? <SkeletonLoading height='1.2rem' width='5rem' br='4px' padding='0' />
                  : <Pagination count={totalPages} page={currentPage} onChange={(_, p) => setCurrentPage(p)} shape="rounded" />
                }
              </Stack>
            </div>
          }

        </div>
      </div>
    </div>
  );
}
